import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import OrgSelector from "../components/OrgSelector";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Plus, Loader2, Calendar, ChevronRight, ArrowLeft,
  AlertTriangle, Eye, CheckCircle2, Lightbulb, Filter, Download, Users
} from "lucide-react";

const SEVERITY_CONFIG = {
  major: { color: "#EF4444", bg: "rgba(239,68,68,0.1)", border: "#EF4444", label: "Major" },
  minor: { color: "#C9A84C", bg: "rgba(201,168,76,0.1)", border: "#C9A84C", label: "Minor" },
  observation: { color: "#3B82F6", bg: "rgba(59,130,246,0.1)", border: "#3B82F6", label: "Observation" },
  opportunity: { color: "#10B981", bg: "rgba(16,185,129,0.1)", border: "#10B981", label: "Opportunity" },
};

const FINDING_STATUS = {
  open: { color: "#EF4444", label: "Open" },
  "in-progress": { color: "#C9A84C", label: "In Progress" },
  closed: { color: "#10B981", label: "Closed" },
};

const AuditsPage = () => {
  const { t } = useTranslation('audit');
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [findings, setFindings] = useState(null);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [severityFilter, setSeverityFilter] = useState("all");
  const [form, setForm] = useState({
    title: "", audit_type: "internal", sector: "life-sciences",
    client_name: "", org_id: "", org_name: "", vendor_id: "", project_id: "", scheduled_date: ""
  });

  useEffect(() => { loadAudits(); }, []);

  const loadAudits = async () => {
    try {
      const res = await axios.get(`${API}/audits`, { withCredentials: true });
      setAudits(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const createAudit = async () => {
    if (!form.title) return;
    setCreating(true);
    try {
      await axios.post(`${API}/audits`, form, { withCredentials: true });
      setShowCreate(false);
      setForm({ title: "", audit_type: "internal", sector: "life-sciences", client_name: "", org_id: "", org_name: "", vendor_id: "", project_id: "", scheduled_date: "" });
      loadAudits();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const viewFindings = async (audit) => {
    setSelectedAudit(audit);
    setSelectedFinding(null);
    setFindingsLoading(true);
    setSeverityFilter("all");
    try {
      const res = await axios.get(`${API}/audits/${audit.audit_id}/findings`, { withCredentials: true });
      setFindings(res.data.findings || []);
    } catch (e) {
      console.error(e);
      setFindings([]);
    }
    setFindingsLoading(false);
  };

  const filteredFindings = findings
    ? severityFilter === "all"
      ? findings
      : findings.filter(f => f.severity === severityFilter)
    : [];

  // Findings detail view (master-detail layout)
  if (selectedAudit && findings !== null) {
    return (
      <QMSLayout title={t('audit:auditsPage.subtitle')}>
        <div data-testid="findings-view">
          {/* Back button */}
          <button onClick={() => { setSelectedAudit(null); setFindings(null); }}
            className="flex items-center gap-2 text-[#8B95A5] text-sm hover:text-white mb-4 transition-colors"
            data-testid="back-to-audits-btn">
            <ArrowLeft size={16} /> Back to Audits
          </button>

          {/* Audit Header */}
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4 mb-4">
            <h2 className="text-white font-['Syne'] font-bold text-lg">{selectedAudit.title}</h2>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-[#8B95A5] text-xs capitalize">{selectedAudit.audit_type} audit</span>
              <span className="text-[#8B95A5] text-xs capitalize">{selectedAudit.sector}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                selectedAudit.status === "completed" ? "bg-[rgba(16,185,129,0.1)] text-[#10B981]" :
                selectedAudit.status === "in-progress" ? "bg-[rgba(201,168,76,0.1)] text-[#C9A84C]" :
                "bg-[rgba(107,114,128,0.1)] text-[#6B7280]"
              }`}>{selectedAudit.status}</span>
            </div>
          </div>

          {/* Severity Summary Scorecard */}
          <div className="grid grid-cols-4 gap-3 mb-4" data-testid="severity-scorecard">
            {[
              { key: "major", label: t('auditsPage.majorNCs'), icon: AlertTriangle },
              { key: "minor", label: t('auditsPage.minorNCs'), icon: AlertTriangle },
              { key: "observation", label: t('auditsPage.observations'), icon: Eye },
              { key: "opportunity", label: t('auditsPage.opportunities'), icon: Lightbulb },
            ].map(s => {
              const sev = SEVERITY_CONFIG[s.key];
              const count = findings.filter(f => f.severity === s.key).length;
              return (
                <div key={s.key} className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-3 flex items-center gap-3" style={{ borderTop: `2px solid ${sev.color}` }}>
                  <div className="w-10 h-10 rounded flex items-center justify-center" style={{ backgroundColor: sev.bg }}>
                    <s.icon size={18} style={{ color: sev.color }} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{count}</p>
                    <p className="text-[#8B95A5] text-xs">{s.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sticky Filter Chips */}
          <div className="sticky top-0 z-10 bg-[#111318] py-2 flex items-center gap-2 mb-4" data-testid="severity-filter-bar">
            <Filter size={14} className="text-[#8B95A5]" />
            {[
              { id: "all", label: t('auditsPage.all'), color: "#8B95A5" },
              { id: "major", label: "Major", color: "#EF4444" },
              { id: "minor", label: "Minor", color: "#C9A84C" },
              { id: "observation", label: "Observation", color: "#3B82F6" },
              { id: "opportunity", label: "Opportunity", color: "#10B981" },
            ].map(s => (
              <button key={s.id} onClick={() => setSeverityFilter(s.id)}
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${
                  severityFilter === s.id ? "text-white" : "text-[#8B95A5] hover:text-white"
                }`}
                style={severityFilter === s.id ? { backgroundColor: `${s.color}20`, color: s.color } : {}}
                data-testid={`severity-filter-${s.id}`}>
                {s.label}
                {s.id !== "all" && <span className="ml-1 opacity-60">({findings.filter(f => f.severity === s.id).length})</span>}
              </button>
            ))}
          </div>

          {findingsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-[#36A9E1]" size={28} />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Master: Findings List (left) */}
              <div className="lg:col-span-2 space-y-2 max-h-[600px] overflow-y-auto" data-testid="findings-master-list">
                {filteredFindings.length === 0 ? (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-8 text-center">
                    <p className="text-[#8B95A5] text-sm">{t('auditsPage.noFindingsMatch')}</p>
                  </div>
                ) : (
                  filteredFindings.map(f => {
                    const sev = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.observation;
                    const st = FINDING_STATUS[f.status] || FINDING_STATUS.open;
                    const isSelected = selectedFinding?.finding_id === f.finding_id;
                    return (
                      <div key={f.finding_id} onClick={() => setSelectedFinding(f)}
                        className={`bg-[#1F222D] rounded-[3px] p-3.5 cursor-pointer transition-all hover:border-[rgba(255,255,255,0.15)] ${
                          isSelected ? "border-l-[3px] border-r border-t border-b border-r-[rgba(255,255,255,0.1)] border-t-[rgba(255,255,255,0.1)] border-b-[rgba(255,255,255,0.1)]" :
                          "border-l-[3px] border-r border-t border-b border-r-[rgba(255,255,255,0.07)] border-t-[rgba(255,255,255,0.07)] border-b-[rgba(255,255,255,0.07)]"
                        }`}
                        style={{ borderLeftColor: sev.border }}
                        data-testid={`finding-item-${f.finding_id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[#38BDF8] text-xs font-mono">{f.clause_reference}</span>
                          <span className="text-xs font-medium" style={{ color: st.color }}>{st.label}</span>
                        </div>
                        <h4 className="text-white text-sm font-medium leading-snug line-clamp-2">{f.title}</h4>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: sev.color, backgroundColor: sev.bg }}>
                            {sev.label}
                          </span>
                          {f.due_date && (
                            <span className="text-[#8B95A5] text-[10px] flex items-center gap-0.5">
                              <Calendar size={9} /> {f.due_date}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Detail: Selected Finding (right) */}
              <div className="lg:col-span-3" data-testid="finding-detail-panel">
                {selectedFinding ? (() => {
                  const sev = SEVERITY_CONFIG[selectedFinding.severity] || SEVERITY_CONFIG.observation;
                  const st = FINDING_STATUS[selectedFinding.status] || FINDING_STATUS.open;
                  return (
                    <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px]">
                      {/* Severity bar */}
                      <div className="h-1 rounded-t-[3px]" style={{ backgroundColor: sev.border }} />
                      <div className="p-5 space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[#38BDF8] text-xs font-mono">{selectedFinding.clause_reference}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ color: sev.color, backgroundColor: sev.bg }}>
                                {sev.label}
                              </span>
                            </div>
                            <h3 className="text-white font-['Syne'] font-bold">{selectedFinding.title}</h3>
                          </div>
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ color: st.color, backgroundColor: `${st.color}15` }}>
                            {st.label}
                          </span>
                        </div>

                        <div>
                          <span className="text-[#8B95A5] text-xs uppercase tracking-wider">Description</span>
                          <p className="text-[#CBD5E1] text-sm mt-1 leading-relaxed">{selectedFinding.description}</p>
                        </div>

                        {selectedFinding.corrective_action && (
                          <div className="bg-[rgba(201,168,76,0.05)] border border-[rgba(201,168,76,0.15)] rounded-[3px] p-3">
                            <span className="text-[#C9A84C] text-xs uppercase tracking-wider flex items-center gap-1">
                              <Lightbulb size={12} /> {t('auditsPage.correctiveAction')}
                            </span>
                            <p className="text-[#CBD5E1] text-sm mt-1">{selectedFinding.corrective_action}</p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[rgba(255,255,255,0.05)]">
                          <div>
                            <span className="text-[#8B95A5] text-xs">{t('auditsPage.assignedTo')}</span>
                            <p className="text-white text-sm mt-0.5">{selectedFinding.assigned_to || t('auditsPage.unassigned')}</p>
                          </div>
                          <div>
                            <span className="text-[#8B95A5] text-xs">{t('auditsPage.dueDate')}</span>
                            <p className="text-white text-sm mt-0.5">{selectedFinding.due_date || t('auditsPage.notSet')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })() : (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-12 text-center">
                    <Eye className="mx-auto mb-3 text-[#8B95A5] opacity-40" size={32} />
                    <p className="text-[#8B95A5] text-sm">{t('auditsPage.selectFinding')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </QMSLayout>
    );
  }

  // Audit list view
  return (
    <QMSLayout title={t('audit:auditsPage.subtitle')}>
      <div className="space-y-6" data-testid="audits-page">
        <div className="flex items-center justify-between">
          <p className="text-[#8B95A5] text-sm">{t('auditsPage.subtitle')}</p>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#36A9E1] hover:bg-[#2d8fbf] text-white text-sm font-medium rounded-[3px]"
            data-testid="create-audit-btn">
            <Plus size={16} /> {t('auditsPage.newAudit')}
          </button>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-md">
              <div className="p-5 border-b border-[rgba(255,255,255,0.07)]">
                <h3 className="font-['Syne'] font-bold text-white">{t('auditsPage.scheduleNew')}</h3>
              </div>
              <div className="p-5 space-y-4">
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="Audit title" className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                  data-testid="audit-title-input" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.audit_type} onChange={e => setForm({...form, audit_type: e.target.value})}
                    className="px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none">
                    <option value="internal">Internal</option>
                    <option value="external">External</option>
                    <option value="certification">Certification</option>
                  </select>
                  <select value={form.sector} onChange={e => setForm({...form, sector: e.target.value})}
                    className="px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none">
                    <option value="life-sciences">Life Sciences</option>
                    <option value="automotive">Automotive</option>
                    <option value="aerospace">Aerospace</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>
                {(form.audit_type === "external" || form.audit_type === "certification") && (
                  <input value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})}
                    placeholder="Client / Organization name"
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none placeholder-[#64748B]"
                    data-testid="audit-client-input" />
                )}
                <OrgSelector
                  value={form.org_id}
                  onChange={(orgId, orgName) => setForm({...form, org_id: orgId, org_name: orgName, client_name: orgName || form.client_name})}
                  showVendorId={form.audit_type === "external" || form.audit_type === "certification"}
                  vendorId={form.vendor_id}
                  onVendorIdChange={v => setForm({...form, vendor_id: v})}
                />
                <input type="date" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})}
                  className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none" />
              </div>
              <div className="p-5 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-[#8B95A5] text-sm">{t('auditsPage.cancel')}</button>
                <button onClick={createAudit} disabled={creating || !form.title}
                  className="px-5 py-2.5 bg-[#36A9E1] disabled:opacity-50 text-white text-sm font-medium rounded-[3px]"
                  data-testid="submit-audit-btn">
                  {creating ? t('auditsPage.creating') : t('auditsPage.create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Audit List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={28} /></div>
        ) : audits.length === 0 ? (
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-12 text-center">
            <p className="text-[#8B95A5]">{t('auditsPage.noAudits')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {audits.map(audit => (
              <div key={audit.audit_id}
                className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-4 flex items-center justify-between hover:border-[rgba(255,255,255,0.15)] transition-colors"
                data-testid={`audit-item-${audit.audit_id}`}>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white text-sm font-medium">{audit.title}</h4>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[#8B95A5] text-xs capitalize">{audit.audit_type}</span>
                    <span className="text-[#8B95A5] text-xs capitalize">{audit.sector}</span>
                    {audit.client_name && (
                      <span className="text-[#38BDF8] text-xs flex items-center gap-1">
                        <Users size={10} /> {audit.client_name}
                      </span>
                    )}
                    {audit.scheduled_date && (
                      <span className="text-[#8B95A5] text-xs flex items-center gap-1">
                        <Calendar size={10} /> {audit.scheduled_date}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      audit.status === "completed" ? "bg-[rgba(16,185,129,0.1)] text-[#10B981]" :
                      audit.status === "in-progress" ? "bg-[rgba(201,168,76,0.1)] text-[#C9A84C]" :
                      "bg-[rgba(107,114,128,0.1)] text-[#6B7280]"
                    }`}>{audit.status}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {audit.findings_count > 0 && (
                    <span className="text-[#8B95A5] text-xs">{audit.findings_count} findings</span>
                  )}
                  <button onClick={() => viewFindings(audit)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(54,169,225,0.08)] text-[#38BDF8] text-xs rounded-[3px] hover:bg-[rgba(54,169,225,0.15)] transition-colors"
                    data-testid={`view-findings-${audit.audit_id}`}>
                    <Eye size={12} /> View Findings
                    <ChevronRight size={12} />
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const res = await axios.get(`${API}/export/audit/${audit.audit_id}/pdf`, {
                          withCredentials: true,
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `audit_report_${audit.audit_id}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (err) {
                        console.error("PDF export error:", err);
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(201,168,76,0.08)] text-[#C9A84C] text-xs rounded-[3px] hover:bg-[rgba(201,168,76,0.15)] transition-colors"
                    data-testid={`export-audit-pdf-${audit.audit_id}`}
                  >
                    <Download size={12} /> PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default AuditsPage;
